import {
	Box,
	Button,
	FormControl,
	FormHelperText,
	Menu,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { createRef, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import {
	getActiveStep,
	getActiveTicket,
	getDetailedViewTicketSummary,
	getEditMode,
	getIsChatOpen,
	getIsTestCasesGenerating,
	getIsTicketShare,
	getIsTicketUpdate,
	getIsUseCasesGenerating,
	getTicketUpdating,
} from 'store/ticket-service/selector';
import {
	setEditMode,
	setHistoryMode,
	setIsChatOpen,
	setSidebarView,
} from 'store/ticket-service/ticketSlice';
import { setIsCreateTicketShareOpen } from 'store/ticket-share-service/ticketShareSlice';
import { setIsExportModalOpen } from 'store/user-service/userSlice';

import { flexMixin } from 'utils/flexMixin';

import { NamespacesList, SharePermissions, Statuses, TicketStages } from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';
import { useGetUser } from 'core/hooks/useGetUser';
import useTooltip from 'core/hooks/useTooltip';
import { TicketMenuItem } from 'core/types/base';

import { ContentType, FormTitleValue } from 'pages/Home';

import { Ticket } from 'components/Ticket';
import { CustomTooltipElement } from 'components/shared/CustomDisabledElement/CustomTooltipElement';
import { TicketUpdateTime } from 'components/shared/ticketUpdateTime';

import { ReactComponent as EditTicket } from 'assets/icons/edit-ticket.svg';
import { ReactComponent as Export } from 'assets/icons/export.svg';
import { ReactComponent as HideChat } from 'assets/icons/hide-chat.svg';
import { ReactComponent as HistoryChanges } from 'assets/icons/history-changes.svg';
import { ReactComponent as ActionMenu } from 'assets/icons/more-vertical.svg';
import { ReactComponent as SaveIcon } from 'assets/icons/save.svg';
import { ReactComponent as Share } from 'assets/icons/share.svg';
import { ReactComponent as ShowChat } from 'assets/icons/show-chat.svg';
import { ReactComponent as ArrowLeft } from 'assets/icons/small-arrow-left.svg';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';
import { ReactComponent as ReportAnIssueIcon } from 'assets/icons/warning-blue.svg';
import { titleTextColor } from 'assets/styles/_palette';

export const TicketDescription = ({ setIsShowSaveModal, setIsDeleteModalOpen }: any) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { t: commonTranslate, i18n } = useTranslation(NamespacesList['common']);
	const titleRef = createRef<HTMLDivElement>();

	const isTooltipVisible = useTooltip(titleRef);
	const { user } = useGetUser();

	const activeStep = useSelector(getActiveStep);
	const editMode = useSelector(getEditMode);
	const activeTicket = useSelector(getActiveTicket);
	const isTicketUpdate = useSelector(getIsTicketUpdate);
	const isTicketUpdating = useSelector(getTicketUpdating);
	const isUseCasesGenerating = useSelector(getIsUseCasesGenerating);
	const isTestCasesGenerating = useSelector(getIsTestCasesGenerating);
	const detailedViewTicketSummary = useSelector(getDetailedViewTicketSummary);
	const isTicketShare = useSelector(getIsTicketShare);
	const isChatOpen = useSelector(getIsChatOpen);

	const updating = useSelector(getTicketUpdating);

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [contentView, setContentView] = useState<ContentType>('Chat');
	const [ticketTitle, setTicketTitle] = useState<string>(activeTicket?.title || '');
	const open = Boolean(anchorEl);

	const {
		reset,
		formState: { errors },
		setError,
	} = useForm<FormTitleValue>();

	const handleSaveTicket = () => {
		if (isTicketUpdate) {
			setIsShowSaveModal(true);
		} else
			errorNotify(
				commonTranslate('errors.noChangesDetectedErrorTitle'),
				commonTranslate('errors.noChangesDetectedErrorDescription')
			);
	};

	const handleTicketStepExport = async () => {
		dispatch(setIsExportModalOpen(true));
	};

	const handleChangesVersioningOpen = () => {
		dispatch(setHistoryMode(true));
	};

	const handleTicketShare = () => {
		dispatch(setIsCreateTicketShareOpen(true));
	};

	const handleSaveButtonClick = () => {
		if (!isTicketUpdating) {
			handleSaveTicket();
			handleClose();
		}
	};

	const handleClickOnMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const activateEditMode = () => {
		dispatch(setEditMode(true));
	};

	const handleHideChat = () => {
		dispatch(setIsChatOpen(!isChatOpen));
	};

	const userHasSubscription = useMemo(
		() => user?.accountPlan !== 0 && user?.accountPlan !== null,
		[user?.accountPlan]
	);

	const isEditModeAvailable = useMemo(() => {
		const ticketPermissions =
			activeTicket?.permissions?.map((permission) => permission.permission) || [];

		const isPermissionGiven =
			ticketPermissions.includes(SharePermissions.Write) ||
			ticketPermissions.includes(SharePermissions.Manage);

		const isEditAvailableForStep =
			activeTicket?.currentStep !== TicketStages.report &&
			activeTicket?.status !== Statuses['Draft'] &&
			!editMode;
		// Toujours autoriser Edit sur l'étape Notifications pour pouvoir supprimer des notifications
		const isEditAvailable =
			isEditAvailableForStep || (activeStep === TicketStages.notifications && !editMode);

		return isTicketShare ? isEditAvailable && isPermissionGiven : isEditAvailable;
	}, [
		isTicketShare,
		editMode,
		activeStep,
		activeTicket?.id,
		activeTicket?.currentStep,
		activeTicket?.status,
	]);

	const isSaveAvailable = useMemo(() => {
		const ticketPermissions =
			activeTicket?.permissions?.map((permission) => permission.permission) || [];

		const isPermissionGiven =
			ticketPermissions.includes(SharePermissions.Write) ||
			ticketPermissions.includes(SharePermissions.Manage);

		return isTicketShare ? isPermissionGiven : true;
	}, [isTicketShare, editMode, activeTicket?.id]);

	const menuItems: TicketMenuItem[] = useMemo(
		() => [
			{
				onClick: handleChangesVersioningOpen,
				title: 'menu.changesVersioning',
				icon: <HistoryChanges style={{ width: '20px', height: '20px' }} />,
				isActive: true,
			},
			/* {
				onClick: handleSaveButtonClick,
				title: 'menu.saveChanges',
				isDisabled: isTicketUpdate && updating,
				icon:
					isTicketUpdate && updating ? (
						<Oval
							height={14}
							width={14}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					) : (
						<SaveIcon style={{ width: '20px', height: '20px' }} />
					),
				isActive: isSaveAvailable,
			}, */
		{
			onClick: () => {
				navigate('/support');
				handleClose();
			},
			title: 'menu.reportIssue',
			icon: <ReportAnIssueIcon style={{ width: '20px', height: '20px' }} />,
			isActive: true,
		},
			{
				onClick: () => {
					activateEditMode();
					handleClose();
				},
				title: 'menu.edit',
				icon: <EditTicket style={{ width: '20px', height: '20px' }} />,
				isDisabled: isTestCasesGenerating || isUseCasesGenerating,
				isActive: isEditModeAvailable,
			},
			{
				onClick: () => {
					setIsDeleteModalOpen(true);
					handleClose();
				},
				title: 'menu.delete',
				icon: <Trash style={{ width: '20px', height: '20px' }} />,
				isActive: !isTicketShare,
			},
			{
				onClick: handleTicketShare,
				title: 'menu.shareTicket',
				icon: <Share style={{ width: '20px', height: '20px' }} />,
				isActive: !isTicketShare,
				isSubscriptionOnly: true,
			},
			{
				onClick: () => handleTicketStepExport(),
				isActive: activeStep !== TicketStages.report,
				title: 'menu.exportStep',
				icon: <Export style={{ width: '20px', height: '20px' }} />,
			},
		],
		[
			isTicketUpdate,
			updating,
			isUseCasesGenerating,
			isTestCasesGenerating,
			activeStep,
			isEditModeAvailable,
			isTicketShare,
		]
	);

	useEffect(() => {
		if (activeTicket) {
			setTicketTitle(activeTicket.title || '');
			reset({
				title: activeTicket.title || '',
			});
		}
	}, [activeTicket?.id]);

	return (
		<>
			<Box className="rtl-able">
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					{window.innerWidth < 900 ? (
						<Button
							sx={{
								height: '44px',
								minWidth: '44px',
								width: '44px',
							}}
							onClick={() => dispatch(setSidebarView(true))}
						>
							<ArrowLeft />
						</Button>
					) : null}

					{editMode ? (
						<FormControl fullWidth error={!!errors?.title}>
							<TextField
								variant="standard"
								fullWidth
								onChange={(e) => {
									setError('title', {
										type: 'manual',
										message: '',
									});
									setTicketTitle(e.target.value);
								}}
								InputProps={{
									disableUnderline: true,
								}}
								inputProps={{
									style: { fontWeight: 700 },
								}}
								defaultValue={activeTicket?.title || ''}
							></TextField>
							<FormHelperText
								sx={{
									fontSize: '12px',
									textAlign: 'left',
									margin: '0',
								}}
							>
								{errors?.title?.message}
							</FormHelperText>
						</FormControl>
					) : (
						<Box>
							<Tooltip
								componentsProps={{
									tooltip: {
										sx: {
											backgroundColor: '#f5f5f9',
											color: 'rgba(0, 0, 0, 0.87)',
											fontSize: '12px',
											maxWidth: 250,
											border: '1px solid #dadde9',
											'& .MuiTooltip-popper': { margin: 0 },
										},
									},
								}}
								PopperProps={{
									modifiers: [
										{
											name: 'offset',
											options: {
												offset: [0, -15],
											},
										},
									],
								}}
								title={
									isTooltipVisible
										? activeTicket?.title.includes('defaultTicketTitle') ||
										  activeTicket?.title.includes('New request')
											? commonTranslate('text.defaultTicketTitle')
											: activeTicket?.title
										: null
								}
								placement="top"
							>
								<Typography
									ref={titleRef}
									sx={{
										fontSize: '24px',
										'@media (max-width: 1536px)': {
											fontSize: '18px',
										},
										fontWeight: '700',
										color: titleTextColor,
										whiteSpace: { xs: 'wrap', md: 'nowrap' },
										textOverflow: 'ellipsis',
										overflow: 'hidden',
										maxWidth: '600px',
										textAlign: 'center',
									}}
								>
									{activeTicket?.title.includes('defaultTicketTitle') ||
									activeTicket?.title.includes('New request')
										? commonTranslate('text.defaultTicketTitle')
										: activeTicket?.title}
								</Typography>
							</Tooltip>
						</Box>
					)}

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							gap: '16px',
						}}
					>
						{activeStep === TicketStages.general || activeStep === TicketStages.notifications ? (
							<Button
								sx={{
									gap: '8px',
									padding: '8px 16px',
									cursor: detailedViewTicketSummary !== null ? 'default' : 'pointer',
									borderRadius: '8px',
									minWidth: '120px',
									textTransform: 'none',
								}}
								variant="outlined"
								disabled={detailedViewTicketSummary !== null}
								onClick={handleHideChat}
							>
								{isChatOpen ? (
									<>
										<HideChat
											style={{
												color: detailedViewTicketSummary === null ? '#4786FF' : '#00000042',
											}}
										/>
										<Typography
											sx={{
												fontWeight: '700',
											}}
										>
											{commonTranslate('buttons.hideChat')}
										</Typography>
									</>
								) : (
									<>
										<ShowChat
											style={{
												color: detailedViewTicketSummary === null ? '#4786FF' : '#00000042',
											}}
										/>
										<Typography
											sx={{
												fontWeight: '700',
											}}
										>
											{commonTranslate('buttons.showChat')}
										</Typography>
									</>
								)}
							</Button>
						) : null}

						<Button sx={{ minWidth: '24px', padding: '0' }} onClick={handleClickOnMenu}>
							<ActionMenu />
						</Button>

						<Menu
							className="rtl-able"
							PaperProps={{
								style: {
									width: 212,
									marginTop: '12px',
								},
							}}
							anchorEl={anchorEl}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: i18n?.language === 'ar' ? 'left' : 'right',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: i18n?.language === 'ar' ? 'left' : 'right',
							}}
							open={open}
							onClose={handleClose}
							onClick={handleClose}
						>
							{menuItems.map((item) =>
								item?.isActive ? (
									!userHasSubscription && item?.isSubscriptionOnly ? (
										<CustomTooltipElement
											message={commonTranslate('tooltips.unavailableForYourSubscriptionLevel')}
											placement="right"
											hoverOnDisabled={false}
										>
											<span>
												<MenuItem key={item.title} disabled={true}>
													<Box
														sx={{
															...flexMixin({ gap: 12 }),
														}}
													>
														{item.icon}
														<Typography>{commonTranslate(item.title)}</Typography>
													</Box>
												</MenuItem>
											</span>
										</CustomTooltipElement>
									) : (
										<MenuItem
											onClick={item.onClick}
											key={item.title}
											disabled={item?.isDisabled ?? false}
										>
											<Box
												sx={{
													...flexMixin({ gap: 12 }),
												}}
											>
												{item.icon}
												<Typography>{commonTranslate(item.title)}</Typography>
											</Box>
										</MenuItem>
									)
								) : null
							)}
						</Menu>
					</Box>
				</Box>
				<TicketUpdateTime
					createDate={activeTicket?.createdDate}
					updateDate={activeTicket?.updatedDate}
				/>
				{window.innerWidth < 900 &&
				![TicketStages.report, TicketStages.useCases, TicketStages.testCases].includes(
					activeStep
				) ? (
					<Box
						sx={{
							height: '48px',
							...flexMixin({ justify: 'space-around' }),
							marginTop: '16px',
							background: '#ECF3FF',
							borderRadius: '40px',
						}}
					>
						<Button
							sx={{
								width: '155px',
								height: '32px',
								textTransform: 'none',
								borderRadius: '40px',
								backgroundColor: contentView === 'Chat' ? '#4786FF !important' : '#ECF3FF',
								color: contentView === 'Chat' ? '#FFF' : '#4786FF',
							}}
							onClick={() => {
								setContentView('Chat');
							}}
						>
							{commonTranslate('buttons.chat')}
						</Button>
						<Button
							sx={{
								width: '155px',
								height: '32px',
								textTransform: 'none',
								borderRadius: '40px',
								backgroundColor: contentView === 'RequestInfo' ? '#4786FF !important' : '#ECF3FF',
								color: contentView === 'RequestInfo' ? '#FFF' : '#4786FF',
							}}
							onClick={() => setContentView('RequestInfo')}
						>
							{commonTranslate('buttons.requestInfo')}
						</Button>
					</Box>
				) : null}
			</Box>

			<Box
				sx={{
					overflow: 'hidden',
					marginTop: { xs: '16px', md: '0' },
					height: '100%',
				}}
			>
				<Ticket contentView={contentView} ticketTitle={ticketTitle} setTitleError={setError} />
			</Box>
		</>
	);
};
